import { ApiResponse } from "./contacts";

// Contact import wizard — shared shape for the multi-step import flow.
// "Upload File" and "Map Fields" are implemented for now; the remaining steps
// are declared here so the stepper renders the full journey.

export interface ContactImportStep {
  id: number;
  label: string;
}

export const CONTACT_IMPORT_STEPS: ContactImportStep[] = [
  { id: 1, label: "Upload File" },
  { id: 2, label: "Map Fields" },
  { id: 3, label: "Dropdowns" },
  { id: 4, label: "Validate" },
  { id: 5, label: "Review" },
];

export const CONTACT_IMPORT_UPLOAD_STEP = 1;
export const CONTACT_IMPORT_MAP_FIELDS_STEP = 2;
export const CONTACT_IMPORT_DROPDOWNS_STEP = 3;
export const CONTACT_IMPORT_VALIDATE_STEP = 4;
export const CONTACT_IMPORT_REVIEW_STEP = 5;

/** Last step with a screen behind it; Continue stops advancing here. */
export const CONTACT_IMPORT_LAST_BUILT_STEP = CONTACT_IMPORT_REVIEW_STEP;

/**
 * Steps counted in the header, matching the total steps in the stepper.
 */
export const CONTACT_IMPORT_TOTAL_STEPS = CONTACT_IMPORT_STEPS.length;

export type ContactImportFormatLabel = "CSV" | "XLSX";

export interface ContactImportFormat {
  label: ContactImportFormatLabel;
  extension: string;
}

// Legacy .xls (pre-2007 binary) is deliberately excluded: no maintained
// npm-hosted parser reads it, so users re-save as .xlsx or .csv instead.
export const CONTACT_IMPORT_FORMATS: ContactImportFormat[] = [
  { label: "CSV", extension: ".csv" },
  { label: "XLSX", extension: ".xlsx" },
];

/** `accept` attribute for the file input / dropzone. */
export const CONTACT_IMPORT_ACCEPT = CONTACT_IMPORT_FORMATS.map(
  (format) => format.extension
).join(",");

/**
 * Approved import ceiling, matching the `maxRows` the upload API reports. Rows
 * past it are dropped rather than silently imported.
 */
export const CONTACT_IMPORT_MAX_ROWS = 5000;

/**
 * How many of each list an import is asked for at a time.
 *
 * The API pages `dropdownColumns[].values`, `ownerMapping.matched/suggestions`
 * and `companyMapping.matched/unresolvedCompany` independently, and this is
 * what one page of each holds. The Dropdowns step asks for another page when
 * the user wants one, rather than pulling every value up front.
 */
export const CONTACT_IMPORT_PAGE_SIZE = 50;


export const CONTACT_IMPORT_MAX_FILE_SIZE_MB = 10;

/**
 * Files are read in the browser, so an oversized upload is rejected before
 * parsing rather than left to exhaust the tab's memory.
 */
export const CONTACT_IMPORT_MAX_FILE_SIZE_BYTES =
  CONTACT_IMPORT_MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * One spreadsheet row keyed by its own column header, exactly as it appeared
 * in the file. Mapping onto CRM contact fields happens in a later step.
 */
export type ContactImportRawRow = Record<string, string>;

/** Result of reading an uploaded file, before any mapping or validation. */
export interface ParsedContactFile {
  fileName: string;
  format: ContactImportFormatLabel;
  /** Column headers, de-duplicated and safe to use as object keys. */
  headers: string[];
  /**
   * Headers the file uses more than once, as written. De-duplicating them for
   * `headers` keeps the rows readable, but an import cannot tell which of two
   * identically named columns a field should come from.
   */
  duplicateHeaders: string[];
  rows: ContactImportRawRow[];
  /** Rows actually returned — capped at CONTACT_IMPORT_MAX_ROWS. */
  rowCount: number;
  columnCount: number;
  /** Non-blank rows found in the file, before the cap was applied. */
  totalRowsInFile: number;
  truncated: boolean;
}

/** File name the downloaded import template is saved under. */
export const CONTACT_IMPORT_TEMPLATE_FILE_NAME = "worklap-contact-import-template.csv";

// ─── Upload API ──────────────────────────────────────────────────────────────

/** The import this file feeds; only contacts are supported so far. */
export const CONTACT_IMPORT_MODULE = "CONTACT";

export type ContactImportModule = typeof CONTACT_IMPORT_MODULE;

/** POST /crm/imports/upload-url body. */
export interface ContactImportUploadUrlRequest {
  module: ContactImportModule;
  originalFileName: string;
  fileSizeBytes: number;
}

/** What the API hands back so the file can be put on S3 directly. */
export interface ContactImportUploadUrlData {
  /** Identifies the uploaded file in later import calls. */
  mediaUuid: string;
  /** Presigned S3 URL — a short-lived credential, never logged or stored. */
  uploadUrl: string;
  s3Key: string;
  expiresInSeconds: number;
  /** Server-side ceilings, which the later steps are held to. */
  maxSizeBytes: number;
  maxRows: number;
}

export type ContactImportUploadUrlResponse = ApiResponse<ContactImportUploadUrlData>;

/**
 * What Step 1 keeps once the file is on S3 and the next step can build on.
 *
 * The presigned URL is deliberately absent: it expires, and it carries a
 * signature that has no business being held in app state.
 */
export interface ContactImportUpload {
  mediaUuid: string;
  s3Key: string;
  fileName: string;
  fileSizeBytes: number;
  maxSizeBytes: number;
  maxRows: number;
}

// ─── Create import API ───────────────────────────────────────────────────────

/** POST /crm/imports body; the mediaUuid comes from the upload-url response. */
export interface ContactImportCreateRequest {
  module: ContactImportModule;
  mediaUuid: string;
}

export type ContactImportJobStatus =
  | "DRAFT"
  | "READY"
  | "PROCESSING"
  | "COMPLETED"
  | "PARTIALLY_COMPLETED"
  | "FAILED";

/**
 * Statuses a started import settles on and will not leave.
 *
 * Anything else means it is still going — including the `DRAFT`/`READY` a
 * caller may still be holding from before Start, which is why finishing is
 * tested for rather than assumed from "not PROCESSING".
 */
export const CONTACT_IMPORT_TERMINAL_STATUSES: ContactImportJobStatus[] = [
  "COMPLETED",
  "PARTIALLY_COMPLETED",
  "FAILED",
];

export const isContactImportFinished = (
  status?: ContactImportJobStatus | null
): boolean => Boolean(status && CONTACT_IMPORT_TERMINAL_STATUSES.includes(status));

/** One uploaded column, with the field the API guessed for it. */
export interface ContactImportColumnInfo {
  /** Header as the API read it — the key a saved mapping is sent under. */
  header: string;
  mappedField: ContactImportFieldKey | null;
  mappedFieldLabel: string | null;
  sampleValues: string[];
}

/** A distinct value found under a dropdown-backed column. */
export interface ContactImportDropdownValueInfo {
  uploadedValue: string;
  occurrences: number;
  /** Applied automatically at validate time when set. */
  suggestedValue: string | null;
  mappedValue: string | null;
}

export interface ContactImportDropdownColumn {
  field: ContactImportFieldKey;
  label: string;
  header: string;
  options: string[];
  values: ContactImportDropdownValueInfo[];
  totalValueCount: number;
  /**
   * Pages this list holds at the size the call asked for. Every `total*Count`
   * on an import carries one of these, and since the app only ever reads page
   * 0, anything above 1 means the list goes on past what arrived.
   */
  totalValuePages?: number;
  /** Unresolved values with neither a suggestion nor a mapping (APISPEC-V2) */
  unresolvedValues?: string[] | null;
  totalUnresolvedCount?: number;
  totalUnresolvedPages?: number;
}

/**
 * A value set aside with the do-not-import sentinel.
 *
 * Carries its own `occurrences` — how many rows in the file hold it — since a
 * value here has left `matched`/`suggestions` and there is nowhere else to
 * read that from. Older responses sent a bare string, so both are accepted.
 */
export type ContactImportDoNotImportValue =
  | string
  | { uploadedValue: string; occurrences?: number };

/** An uploaded Owner value resolved to a CRM user. */
export interface ContactImportOwnerMatch {
  uploadedValue: string;
  occurrences: number;
  matchedUserUuid: string;
  matchedUserName: string;
  /**
   * Set on a suggestion the API reached by approximate name matching — a typo
   * close enough to point at one user. Always false on an exact match, so a
   * guess is never presented with the confidence of a settled one.
   */
  fuzzyMatch?: boolean;
}

export interface ContactImportOwnerMapping {
  header: string;
  affectedRecordCount: number;
  /** Exact email matches, applied without asking but still overridable. */
  matched?: ContactImportOwnerMatch[];
  totalMatchedCount: number;
  totalMatchedPages?: number;
  /** Name-only matches, which stay blocked until confirmed. */
  suggestions?: ContactImportOwnerMatch[];
  totalSuggestionCount: number;
  totalSuggestionPages?: number;
  /** Sample of actual unresolved values (APISPEC-V2) */
  unresolvedOwners?: string[];
  totalUnresolvedCount?: number;
  totalUnresolvedPages?: number;
  /**
   * Values set aside with the do-not-import sentinel: their rows are left out
   * unless a bulk fallback decided afterwards, which overrides them.
   *
   * `totalDoNotImportCount` counts distinct values, not rows.
   */
  doNotImportOwners?: ContactImportDoNotImportValue[];
  totalDoNotImportCount?: number;
  totalDoNotImportPages?: number;
  resolution: { action: string } | null;
}

/** An uploaded Company value resolved to an existing Company. */
export interface ContactImportCompanyMatch {
  uploadedValue: string;
  occurrences: number;
  matchedCompanyUuid: string;
  matchedCompanyName: string;
}

/**
 * A Company value the API has a record in mind for but has not applied.
 *
 * Shaped apart from `matched[]` on purpose: `suggested*` is what the API
 * proposes, and `confirmedCompanyUuid` is what the user has already agreed to
 * on an earlier visit — which outranks the suggestion when it is set.
 */
export interface ContactImportCompanySuggestion {
  uploadedValue: string;
  occurrences: number;
  suggestedCompanyUuid: string;
  suggestedCompanyName: string;
  /** Null while the suggestion is still only a guess. */
  confirmedCompanyUuid?: string | null;
  /** Set on a match the API reached by approximate name matching. */
  fuzzyMatch?: boolean;
}

export interface ContactImportCompanyMapping {
  header: string;
  affectedContactCount: number;
  notFoundContactCount: number;
  missingContactCount: number;
  matched?: ContactImportCompanyMatch[];
  totalMatchedCount: number;
  totalMatchedPages?: number;
  /** Name matches the API will not apply by itself — see the type's note. */
  suggestions?: ContactImportCompanySuggestion[];
  totalSuggestionCount: number;
  totalSuggestionPages?: number;
  /** Renamed in APISPEC-V2 from unresolvedSample */
  unresolvedCompany?: string[];
  /** Backwards compatibility for unresolvedSample */
  unresolvedSample?: string[];
  totalUnresolvedCount: number;
  totalUnresolvedPages?: number;
  /** Values set aside with the sentinel — see the Owner equivalent. */
  doNotImportCompany?: ContactImportDoNotImportValue[];
  totalDoNotImportCount?: number;
  totalDoNotImportPages?: number;
  resolution: {
    action: string;
    companyUuid?: string;
    companyName?: string;
  } | null;
}

/**
 * Which bucket a row ended up in. Mirrors the row statuses the review summary
 * counts, so filtering the error log by one lines up with its figure.
 *
 * `FAILED` has no counterpart there: it is a row that passed review and then
 * fell over at creation time.
 */
export type ContactImportErrorCategory =
  | "DUPLICATE"
  | "INVALID"
  | "MISSING_MANDATORY"
  | "FAILED";

export interface ContactImportReviewError {
  rowNumber: number;
  field: string;
  message: string;
  rejectedValue: string | null;
  /** Absent on errors the API stored before it started recording this. */
  category?: ContactImportErrorCategory | null;
}

/**
 * One page of an import's stored error log.
 *
 * `errorSample`/`rowErrorSample` on the job are only the first hundred; this is
 * what the rest is read from, and `totalCount` honours whatever `category` the
 * page was asked for.
 */
export interface ContactImportErrorPage {
  items: ContactImportReviewError[];
  page: number;
  size: number;
  totalCount: number;
  totalPages: number;
}

export type ContactImportErrorsResponse = ApiResponse<ContactImportErrorPage>;

export interface ContactImportReviewSummary {
  totalRecords: number;
  readyToImport: number;
  duplicateRecords: number;
  missingMandatoryInformation: number;
  invalidValues: number;
  excludedByChoice: number;
  unmappedColumns: string[];
  unmappedDropdownValues: string[];
  unmappedCompanyValues: string[];
  /** A preview — the first 100. `totalErrorCount` says how many there are. */
  errorSample: ContactImportReviewError[];
  /** Size of the stored log, so a truncated preview can say so. */
  totalErrorCount?: number;
  /**
   * Why `/start` would be refused as things stand, in the API's own words, or
   * null when it would not be. Worked out once when the summary is built and
   * returned as-is, so it is read rather than derived from the counts.
   */
  blockingIssueMessage?: string | null;
  hasBlockingIssues?: boolean;
}

/**
 * The import the API opens for an uploaded file, and returns again from every
 * call that changes it.
 *
 * The counts stay null until the import has run, and the parsed detail is
 * dropped once it is finishing — the file is deliberately not re-read then.
 */
export interface ContactImportRecord {
  uuid: string;
  /** Echoed back by the API, so it is read as given rather than narrowed. */
  module: string;
  status: ContactImportJobStatus;
  originalFileName: string;
  importedByName: string | null;
  importedByEmail: string | null;
  totalRows: number;
  createdCount: number | null;
  skippedCount: number | null;
  failedCount: number | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  failureReason: string | null;
  columns: ContactImportColumnInfo[] | null;
  dropdownColumns: ContactImportDropdownColumn[] | null;
  companyMapping: ContactImportCompanyMapping | null;
  ownerMapping: ContactImportOwnerMapping | null;
  reviewSummary: ContactImportReviewSummary | null;
  /**
   * What actually went wrong on the last run, row by row — as opposed to
   * `reviewSummary.errorSample`, which is the dry run's findings and goes stale
   * the moment the mapping changes after it. Null until the job has been
   * started at least once, and a preview of the first 100 either way.
   */
  rowErrorSample?: ContactImportReviewError[] | null;
  /** Size of the stored log behind `rowErrorSample`. */
  totalErrorCount?: number;
}

export type ContactImportCreateResponse = ApiResponse<ContactImportRecord>;

/**
 * PUT /crm/imports/{uuid}/mapping body.
 *
 * Keys are the file's headers exactly as the API read them; values are the
 * field each one feeds. The call **merges** into the saved mapping: a header
 * left out keeps what it had, and only an explicit `null` un-maps one.
 */
export interface ContactImportMappingRequest {
  columnMapping: ContactImportFieldMap;
}

export type ContactImportMappingResponse = ApiResponse<ContactImportRecord>;

/**
 * Which block of the Dropdowns screen a value-mapping call is for.
 *
 * The API stops inferring it from the field names in the body: every request
 * names its block, and a field belonging to another one is refused.
 */
export type ContactImportValueMappingType = "DROPDOWN" | "OWNER" | "COMPANY";

/**
 * PUT /crm/imports/{uuid}/value-mapping body.
 *
 * One block at a time: `{ type: "OWNER", valueMapping: { CONTACT_OWNER_EMAIL:
 * { "<uploaded value>": "<uuid or enum>" } } }`. Saving a block leaves every
 * other field alone, so a screen sends only what it asked about — and sends
 * that block in full.
 */
export interface ContactImportValueMappingRequest {
  type: ContactImportValueMappingType;
  valueMapping: Partial<Record<ContactImportFieldKey, Record<string, string>>>;
}

export type ContactImportValueMappingResponse = ApiResponse<ContactImportRecord>;

/**
 * How the import handles rows whose Owner could not be resolved — every blank,
 * unknown email and ambiguous name at once, never row by row.
 */
export type ContactImportOwnerFallbackAction = "ASSIGN_ME" | "SKIP";

/** POST /crm/imports/{uuid}/owner-fallback body. */
export interface ContactImportOwnerFallbackRequest {
  action: ContactImportOwnerFallbackAction;
}

export type ContactImportOwnerFallbackResponse = ApiResponse<ContactImportRecord>;

/**
 * How the import handles contacts whose Company could not be resolved.
 *
 * An import never creates a Company: the whole set is either linked to one
 * that already exists, or left out.
 */
export type ContactImportCompanyFallbackAction = "LINK_TO_EXISTING" | "SKIP";

/** POST /crm/imports/{uuid}/company-fallback body. */
export interface ContactImportCompanyFallbackRequest {
  action: ContactImportCompanyFallbackAction;
  /** Required by LINK_TO_EXISTING, and refused without a real company. */
  companyUuid?: string;
}

export type ContactImportCompanyFallbackResponse = ApiResponse<ContactImportRecord>;

/**
 * Why a file that was read successfully still cannot be imported — the file is
 * intact, but its content does not carry what an import needs.
 *
 * The copy lives here rather than in the step so a later check (missing
 * required headers, say) can produce this same shape and reuse the UI as is.
 */
export interface ContactImportIncompatibility {
  /** Replaces the step's description line. */
  description: string;
  /** Headline of the message block. */
  title: string;
  /** What is wrong and what to do about it. */
  message: string;
  /** Which of the two stats the problem is about, so the UI can flag it. */
  highlight: "rows" | "columns";
}

// ─── Map Fields step ─────────────────────────────────────────────────────────

/**
 * The contact fields an uploaded column can feed.
 *
 * Taken from `ContactFormValues` — the shape the CRM already creates and
 * updates contacts with — so a mapping names a field that really exists.
 * `Extract` keeps that honest: a key the form no longer has stops compiling.
 */
export type ContactImportFieldKey =
  | "CONTACT_FIRST_NAME"
  | "CONTACT_LAST_NAME"
  | "CONTACT_EMAIL"
  | "CONTACT_PHONE"
  | "CONTACT_JOB_TITLE"
  | "CONTACT_COMPANY_NAME"
  | "CONTACT_COMPANY_WEBSITE_URL"
  | "CONTACT_LINKEDIN_URL"
  | "CONTACT_LIFECYCLE_STAGE"
  | "CONTACT_ENGAGEMENT_STATUS"
  | "CONTACT_OWNER_EMAIL"
  | "CONTACT_TAGS"
  | "CONTACT_INSIGHTS";

/** A contact field that an uploaded column can be mapped onto. */
export interface ContactImportTargetField {
  /** The contact field's own key, used as the dropdown's value. */
  value: ContactImportFieldKey;
  label: string;
  /** Required fields have to be mapped before the import can run. */
  required?: boolean;
}

/**
 * Fields offered in the Map Fields dropdowns, in the order the import's own
 * template lists them. Labels are the import API's wording, so a template
 * column and its dropdown entry read the same.
 *
 * `required` is about the column, not its contents: the file has to carry a
 * column for each of these fields, but the cells under it may be empty. Whether
 * an individual row has a value is settled later, when the import is validated.
 */
export const CONTACT_IMPORT_TARGET_FIELDS: ContactImportTargetField[] = [
  { value: "CONTACT_FIRST_NAME", label: "First Name", required: true },
  { value: "CONTACT_LAST_NAME", label: "Last Name" },
  { value: "CONTACT_EMAIL", label: "Email", required: true },
  { value: "CONTACT_PHONE", label: "Phone" },
  { value: "CONTACT_JOB_TITLE", label: "Job Title", required: true },
  { value: "CONTACT_COMPANY_NAME", label: "Company", required: true },
  { value: "CONTACT_COMPANY_WEBSITE_URL", label: "Company Website" },
  { value: "CONTACT_LINKEDIN_URL", label: "LinkedIn" },
  { value: "CONTACT_LIFECYCLE_STAGE", label: "Lifecycle Stage" },
  { value: "CONTACT_ENGAGEMENT_STATUS", label: "Engagement Status" },
  { value: "CONTACT_OWNER_EMAIL", label: "Owner", required: true },
  { value: "CONTACT_TAGS", label: "Tags" },
  { value: "CONTACT_INSIGHTS", label: "Insights" },
];

/** Required fields, which have to be mapped before the import can run. */
export const CONTACT_IMPORT_REQUIRED_FIELDS = CONTACT_IMPORT_TARGET_FIELDS.filter(
  (field) => field.required
);


/** Field value for a column the user has chosen to leave out of the import. */
export const CONTACT_IMPORT_NO_FIELD = "none";

/**
 * A column nobody has decided about yet — neither given a field nor skipped.
 *
 * Kept apart from CONTACT_IMPORT_NO_FIELD so the screen can tell "we could not
 * match this, over to you" from "the user said leave it out". Both end up out
 * of the import; only the second one was chosen.
 */
export const CONTACT_IMPORT_UNSET_FIELD = "";

/**
 * A value the user has chosen to leave unmapped, rather than one they have yet
 * to answer for.
 *
 * The two have to be told apart somehow: an untouched value holds `""` and
 * still owes an answer, while this one is settled and lets the step move on.
 * It never leaves the screen — what the API is told is CONTACT_IMPORT_NONE.
 */
export const CONTACT_IMPORT_NO_VALUE = "__NO_VALUE__";

/**
 * The API's own sentinel for "leave every row carrying this value out of the
 * import" (APISPEC §8.5 Blocks 2 and 3).
 *
 * Sent for every block, Lifecycle Stage and Engagement Status included. Note
 * that §10 documents the API refusing it for an enum column, where it is
 * treated as a value that is not one of that column's options — so a 400 on
 * one of those two is the first thing to look at.
 */
export const CONTACT_IMPORT_NONE = "NONE";

export type ContactImportNoField = typeof CONTACT_IMPORT_NO_FIELD;
export type ContactImportUnsetField = typeof CONTACT_IMPORT_UNSET_FIELD;

/** What a column's dropdown can hold: a contact field, a skip, or no answer. */
export type ContactImportMappingField =
  | ContactImportFieldKey
  | ContactImportNoField
  | ContactImportUnsetField;

/**
 * How a column's mapping stands, shown in the Mapping Status column.
 *
 * `AUTO` — settled, either an exact match or a field the user picked. Reads as
 *   "Mapped" on screen: what matters to the user is that the column has a
 *   field, not how it got one.
 * `SUGGESTED` — a likely match worth a glance, from a column named differently.
 * `AMBIGUOUS` — names a field another column already feeds, so it is left for
 *   the user to settle rather than guessed at.
 * `UNMAPPED` — no field, whether unrecognised or deliberately left out.
 */
export type ContactImportMappingStatus =
  | "AUTO"
  | "SUGGESTED"
  | "AMBIGUOUS"
  | "UNMAPPED";

/** One row of the Map Fields step — a file column and the field it feeds. */
export interface ContactImportColumnMapping {
  /** Header exactly as it appeared in the uploaded file. */
  fileColumn: string;
  /** A contact field's key, or CONTACT_IMPORT_NO_FIELD to skip the column. */
  field: ContactImportMappingField;
  status: ContactImportMappingStatus;
}

/**
 * The mapping as it goes to the API: file column → contact field.
 *
 * `null` is meaningful — the API merges what it is sent, so a column left out
 * keeps whatever it was mapped to before. Un-mapping one has to be said out
 * loud.
 */
export type ContactImportFieldMap = Record<string, ContactImportFieldKey | null>;

/**
 * What reading the uploaded file's headers settled: whether every required
 * contact field has a column behind it, and the mapping the Map Fields step
 * starts from.
 */
export interface ContactImportHeaderAnalysis {
  isValid: boolean;
  /** Required fields no column was matched to. */
  missingFields: ContactImportTargetField[];
  /** One row per file column, the file's own header kept as given. */
  mappings: ContactImportColumnMapping[];
}

// ─── Dropdowns step ──────────────────────────────────────────────────────────

/**
 * How sure the import is about the option a value was matched to.
 *
 * `EXACT` — settled without asking (an email for Owner, an exact name for
 *   Company, a known enum value).
 * `SUGGESTED` — a confident guess from a name with no email behind it, which
 *   the API holds as unconfirmed until the user agrees.
 * `FUZZY` — an approximate name match, e.g. a typo. Same standing as
 *   `SUGGESTED`, flagged apart so it can be shown for what it is.
 */
export type ContactImportValueConfidence = "EXACT" | "SUGGESTED" | "FUZZY";

/** One value found in the file and the CRM option it is imported as. */
export interface ContactImportDropdownValue {
  /** Value exactly as it appeared in the file. */
  fileValue: string;
  /** The Worklap option it maps onto; empty when nothing matched. */
  targetValue: string;
  /** Rows in the file carrying this value, so the weight of it is visible. */
  occurrences?: number;
  /** Set when the value needs a look — nothing matched, or only a guess did. */
  notFound?: boolean;
  /** Absent where the idea does not apply, e.g. a plain enum value. */
  confidence?: ContactImportValueConfidence;
}

/** One choice in a value's dropdown. */
export interface ContactImportDropdownOption {
  /** What the mapping is saved as — a uuid, or an enum constant. */
  value: string;
  label: string;
}

/** Badge on a field's card, saying why its values need a decision. */
export type ContactImportDropdownKind = "REQUIRED" | "OPTIONAL" | "DROPDOWN";

/** One of the mutually exclusive ways to handle a field's unmatched values. */
export interface ContactImportDropdownChoice {
  value: string;
  title: string;
  description: string;
}

/**
 * The decision a field needs about values that could not be matched, offered
 * either as inline actions or as a set of exclusive choices.
 */
export interface ContactImportDropdownDecision {
  message: string;
  /** `warning` tints the block; `info` leaves it plain. */
  tone: "warning" | "info";
  /**
   * Buttons that answer the decision in one go, e.g. "Assign me as Owner".
   * The value is the action the API is told about.
   */
  actions?: ContactImportDropdownOption[];
  choices?: ContactImportDropdownChoice[];
  /** A choice that cannot be sent until a record is picked for it. */
  selection?: ContactImportDropdownSelection;
}

/** The record a decision has to be pointed at, e.g. the company to link to. */
export interface ContactImportDropdownSelection {
  /** The choice this belongs to, e.g. "LINK_TO_EXISTING". */
  forChoice: string;
  label: string;
  placeholder: string;
  options: ContactImportDropdownOption[];
  /** What the user picked, once they have. */
  value?: string;
}

/**
 * A dropdown-backed contact field whose values need mapping, listing only the
 * values that actually appear in the uploaded file.
 */
export interface ContactImportDropdownField {
  /**
   * The contact field this card maps values for. Identity everywhere — the
   * label is for display only, so renaming one never moves a card.
   */
  key: ContactImportFieldKey;
  /** Worklap field label, e.g. "Lifecycle Stage". */
  label: string;
  kind: ContactImportDropdownKind;
  /** Distinct values the file holds, which can exceed the rows shown. */
  uniqueValueCount: number;
  /**
   * Whether the API says this card's lists carry on past the page that arrived,
   * taken from their `total*Pages`. Left unset when it did not say, and the
   * rows shown are then measured against `uniqueValueCount` instead.
   */
  hasMorePages?: boolean;
  values: ContactImportDropdownValue[];
  /**
   * Options every dropdown on this card offers. The value is what gets sent —
   * a record's uuid for Owner and Company, the enum constant for the rest — so
   * two people sharing a name stay distinct.
   */
  options: ContactImportDropdownOption[];
  /** Shown by a dropdown with nothing matched, e.g. "No match in Worklap". */
  placeholder: string;
  /** Unmatched values folded behind the "+ N more values" link. */
  moreNotFoundCount?: number;
  /** Line that needs attention, flagged with an icon. */
  alert?: string;
  decision?: ContactImportDropdownDecision;
  /** Closing muted line under the card. */
  note?: string;
  /**
   * Records whose value the import could not resolve. They are never listed —
   * no option exists to offer them — so this count is what says the decision
   * under the rows still has something to answer for.
   */
  unresolvedCount?: number;
  /** The choice the user picked from `decision.choices`, or a decision action. */
  selectedChoice?: string;
  /**
   * Set when the card was built from the import itself rather than standing in
   * for one. Only these are saved back — a placeholder has nothing to send.
   */
  isFromApi?: boolean;
}

/**
 * One screen of the Dropdowns step. The step asks about its fields a few at a
 * time rather than all at once, so each decision is made in isolation.
 */
export interface ContactImportDropdownSubStep {
  id: number;
  /** Short name for the sub-step's own progress band. */
  label: string;
  /** Contact fields whose cards appear together on this screen. */
  fields: ContactImportFieldKey[];
}

/**
 * Owner and Company each get a screen of their own — both carry a decision
 * about unmatched values. Lifecycle Stage and Engagement Status are plain enum
 * matches, so they share the last one.
 */
export const CONTACT_IMPORT_DROPDOWN_SUBSTEPS: ContactImportDropdownSubStep[] = [
  { id: 1, label: "Owner", fields: ["CONTACT_OWNER_EMAIL"] },
  { id: 2, label: "Company", fields: ["CONTACT_COMPANY_NAME"] },
  {
    id: 3,
    label: "Lifecycle & Engagement",
    fields: ["CONTACT_LIFECYCLE_STAGE", "CONTACT_ENGAGEMENT_STATUS"],
  },
];

export const CONTACT_IMPORT_FIRST_DROPDOWN_SUBSTEP =
  CONTACT_IMPORT_DROPDOWN_SUBSTEPS[0].id;

export const CONTACT_IMPORT_LAST_DROPDOWN_SUBSTEP =
  CONTACT_IMPORT_DROPDOWN_SUBSTEPS[CONTACT_IMPORT_DROPDOWN_SUBSTEPS.length - 1].id;
